import React, { useState } from "react";
import axios from "axios";
import "./SecuritySettings.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const SecuritySettings = ({ setMessage }) => {
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Loading state
  const [saving, setSaving] = useState(false);

  // Account deletion confirmation
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Password change handlers
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Password update
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      setSaving(false);
      return;
    }

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/users/profile`,
        { password: passwordData.newPassword },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Reset form
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        // Show success message
        setMessage({
          type: "success",
          text: "Password updated successfully!",
        });
      }
    } catch (error) {
      console.error("Failed to update password:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.error || "Failed to update password",
      });
    } finally {
      setSaving(false);

      // Auto-clear message after 5 seconds
      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 5000);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = () => {
    // This would actually call the API to delete the account
    // For now, it's just a mock function
    setMessage({ 
      type: "warning", 
      text: "This is a demo feature. In a real implementation, your account would be deleted." 
    });
    setShowDeleteConfirmation(false);
    setDeleteConfirmText("");
    
    // Actual implementation would look like:
    /*
    try {
      await axios.delete(`${API_BASE_URL}/api/users/profile`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      // Handle successful deletion (e.g., log out, redirect)
      
    } catch (error) {
      console.error("Failed to delete account:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.error || "Failed to delete account",
      });
    }
    */
  };

  return (
    <div className="security-settings">
      <form onSubmit={handlePasswordUpdate} className="settings-form">
        <div className="settings-section">
          <h3>Change Password</h3>
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <input
              type="password"
              id="currentPassword"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              required
              minLength="10"
            />
            <small className="form-hint">
              Password must be at least 10 characters long and include
              uppercase, lowercase, numbers, and special characters.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              required
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="save-settings-btn"
              disabled={saving}
            >
              {saving ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>
      </form>

      <div className="settings-section">
        <h3>Account Actions</h3>
        <div className="danger-zone">
          <h4>Delete Account</h4>
          <p className="danger-text">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          
          {!showDeleteConfirmation ? (
            <button 
              type="button" 
              className="delete-account-btn"
              onClick={() => setShowDeleteConfirmation(true)}
            >
              Delete Account
            </button>
          ) : (
            <div className="delete-confirmation">
              <p>To confirm, type "DELETE" in the field below:</p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
              />
              <div className="confirmation-actions">
                <button 
                  type="button" 
                  className="cancel-delete-btn"
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setDeleteConfirmText("");
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="confirm-delete-btn"
                  disabled={deleteConfirmText !== "DELETE"}
                  onClick={handleDeleteAccount}
                >
                  Permanently Delete Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;